// refZuVorbelegung.ts — „+ Weitere Informationen erfassen" (Plan 2026-10-01,
// User-Entscheidungen 02.10.2026): Was in einer Schnellanlage steht, wandert
// vorbelegt in die volle Kontaktanlage (`openContactFormWindow`).
//
// Rein und ohne Netz: Der Rechtsform-Katalog kommt als Argument, weil seine
// Ids zwischen DEV und PROD abweichen und nicht fest verdrahtet werden dürfen
// (wie `visitenkarteZuFirmaFormState`). Die Schlüssel sind die BINDING-Keys der
// Anlage-Masken (`postalcode` klein, `name` bei der Firma, `lastname` bei der
// Person) — ein falscher Schlüssel landet im State, aber nie im Feld.

import { findeKatalogEintrag } from "@kmvs/forms-core";
import type { ContactRef } from "../components/relationsInline/types";
import { weitereBranchenNormalisieren } from "../components/relationsInline/types";
import type { Ownership, SharedAddress } from "../components/relationsInline/persistRelations";
import { rechtsformAusName, type LegalFormOption } from "./legalFormOptions";
import type { FormState } from "./formUtils";
import { geschlechtAusAnrede } from "./anredeGeschlecht";
import { verantwortungVon } from "./bearbeitenBeziehungen";

export type VollanlageArt = "private" | "company";

export type Vorbelegung = {
  kind: VollanlageArt;
  initialValues: Record<string, string>;
};

/** Titel der Personen-Maske — sie bietet nur diese drei an (`TITLE_OPTIONS`). */
const MASKEN_TITEL = ["Dr.", "Prof.", "Prof. Dr."];

const t = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** Nur gesetzte Werte übernehmen — leere Schlüssel würden Masken-Defaults überschreiben. */
function setze(ziel: Record<string, string>, key: string, wert: unknown): void {
  const w = typeof wert === "number" ? String(wert) : t(wert);
  if (w) ziel[key] = w;
}

const gleich = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Rechtsform einer Schnellanlage im Katalog finden. Reihenfolge:
 * 1. die gewählte Katalog-Id,
 * 2. die Bezeichnung (Name oder Kürzel) — so kommt der Verein aus der
 *    Vereinsliste an, der nur „Eingetragener Verein (e.V.)" mitbringt,
 * 3. beim Verein der Katalogeintrag des e.V.,
 * 4. der Firmenname, aber nur bei eindeutigem Treffer („… GmbH").
 * Mehrdeutig oder unbekannt → nichts; die Maske fragt dann selbst.
 */
export function rechtsformAufloesen(
  ref: Pick<ContactRef, "legalForm" | "legalFormId" | "lastName">,
  optionen: LegalFormOption[],
  verein = false,
): LegalFormOption | null {
  if (ref.legalFormId) {
    const perId = optionen.find((o) => o.id === Number(ref.legalFormId));
    if (perId) return perId;
    // Katalog (noch) nicht geladen: Id und Bezeichnung trotzdem weitergeben.
    if (t(ref.legalForm)) return { id: Number(ref.legalFormId), legalForm: t(ref.legalForm) };
  }
  const label = t(ref.legalForm);
  if (label) {
    const perName = optionen.find((o) => gleich(o.legalForm, label) || (!!o.kuerzel && gleich(o.kuerzel, label)));
    if (perName) return perName;
  }
  if (verein) {
    const ev = findeKatalogEintrag("EV", optionen);
    if (ev) return ev;
  }
  const name = t(ref.lastName);
  if (name && optionen.length) {
    const { befund, option } = rechtsformAusName(name, optionen);
    if (option && befund.konfidenz === "SICHER" && !befund.trunkiert) return option;
  }
  return null;
}

/**
 * ContactRef bzw. Schnellanlage-Werte → Art der Vollanlage + vorbelegte Felder.
 */
export function refZuVorbelegung(
  ref: ContactRef,
  kontext: { rechtsformOptionen?: LegalFormOption[]; verein?: boolean } = {},
): Vorbelegung {
  const v: Record<string, string> = {};
  // Anschrift — beide Masken lesen dieselben Schlüssel.
  setze(v, "street", ref.street);
  setze(v, "postalcode", ref.postalCode);
  setze(v, "city", ref.city);
  setze(v, "cityArea", ref.cityArea || ref.ortsteil);
  setze(v, "country", ref.country);

  if (ref.type === "Firma") {
    setze(v, "name", ref.lastName);
    const rf = rechtsformAufloesen(ref, kontext.rechtsformOptionen ?? [], !!kontext.verein);
    if (rf) {
      v.gesellschaftsform = rf.legalForm;
      v.legalFormId = String(rf.id);
    }
    setze(v, "rechtsform", ref.rechtsform);
    if (ref.branchengruppeId) v.branchengruppeId = String(ref.branchengruppeId);
    setze(v, "brancheFreitext", ref.brancheFreitext);
    setze(v, "industry", ref.branche);
    const weitere = weitereBranchenNormalisieren(ref.weitereBranchengruppen);
    if (weitere.length) v.weitereBranchengruppen = JSON.stringify(weitere);
    setze(v, "phone", ref.phone);
    setze(v, "mobile", ref.mobile);
    setze(v, "email", ref.email);
    setze(v, "website", ref.web);
    setze(v, "fax", ref.fax);
    setze(v, "umsatzsteuernummer", ref.umsatzsteuernummer);
    setze(v, "register", ref.register);
    setze(v, "handelsregisterNummer", ref.handelsregisterNummer);
    setze(v, "registergericht", ref.registergericht);
    return { kind: "company", initialValues: v };
  }

  setze(v, "firstname", ref.firstName);
  setze(v, "lastname", ref.lastName);
  setze(v, "anrede", ref.salutationLabel);
  const titel = t(ref.titleLabel);
  const maskenTitel = MASKEN_TITEL.find((m) => gleich(m, titel));
  if (maskenTitel) v.title = maskenTitel;
  setze(v, "dateofbirth", ref.birthDate);
  // Kanäle getrennt nach privat/geschäftlich; die einfachen Altfelder
  // (`phone`/`mobile`/`email`) gelten wie in `buildPersonCommItems` als
  // geschäftlich.
  setze(v, "phonePrivate", ref.phonePrivate);
  setze(v, "mobilePrivate", ref.mobilePrivate);
  setze(v, "emailPrivate", ref.emailPrivate);
  setze(v, "phoneWork", ref.phoneWork || ref.phone);
  setze(v, "mobileWork", ref.mobileWork || ref.mobile);
  setze(v, "emailWork", ref.emailWork || ref.email);
  if (Array.isArray(ref.weitereKommunikation)) {
    const wege = ref.weitereKommunikation.filter((w) => t(w?.value));
    if (wege.length) v.weitereKommunikation = JSON.stringify(wege);
  }
  return { kind: "private", initialValues: v };
}

/**
 * Verantwortlichkeit des Elternkontakts als vorbelegte FO/BO-Felder. Die
 * Ownership-Karte der Maske füllt nur LEERE Felder vor — was hier steht, bleibt.
 */
export function ownershipZuVorbelegung(o: Ownership | null | undefined): Record<string, string> {
  const v: Record<string, string> = {};
  if (!o) return v;
  const fo = t(o.foUserId);
  if (fo) {
    v.foUserId = fo;
    const liste = (o.foUserIds ?? []).map(t).filter(Boolean);
    v.foUserIds = liste.length > 1 && liste[0] === fo ? [...new Set(liste)].join(",") : fo;
  }
  setze(v, "boType", o.boType);
  if (o.boType === "TEAM") setze(v, "boTeamId", o.boTeamId);
  if (o.boType === "USER") setze(v, "boUserId", o.boUserId);
  setze(v, "inhouseContactUserId", o.inhouseContactUserId);
  return v;
}

/**
 * Verantwortlichkeit aus einem GELADENEN Kontakt (GET /contact/:id). Ein
 * persönlicher Kontakt vererbt nichts (C8, wie `ownershipAusFormular`).
 */
export function ownershipAusKontakt(c: unknown): Ownership {
  if (!c || typeof c !== "object") return {};
  if ((c as Record<string, unknown>).persoenlich === true) return {};
  return verantwortungVon(c as Record<string, unknown>);
}

/** Bezug zum Elternkontakt, der mit dem POST der Vollanlage mitgeht. */
export type ZusatzPayload = {
  verantwortungVonKontaktId?: number;
  imHaushaltVon?: number;
};

/**
 * Den Bezug an einen Anlage-Payload hängen. Nur gesetzte, gültige Ids — der
 * Server prüft sie ohnehin (Mandant, Lesbarkeit), ein `undefined` im Body
 * soll aber gar nicht erst ankommen.
 */
export function mitZusatzPayload<T extends Record<string, unknown>>(payload: T, zusatz?: ZusatzPayload | null): T {
  if (!zusatz) return payload;
  const von = gueltigeId(zusatz.verantwortungVonKontaktId);
  const haushalt = gueltigeId(zusatz.imHaushaltVon);
  if (!von && !haushalt) return payload;
  return {
    ...payload,
    ...(von ? { verantwortungVonKontaktId: von } : {}),
    ...(haushalt ? { imHaushaltVon: haushalt } : {}),
  };
}

const gueltigeId = (id: unknown): number | undefined =>
  typeof id === "number" && Number.isInteger(id) && id > 0 ? id : undefined;

export type VollanlageEingabe = {
  ref: ContactRef;
  rechtsformOptionen?: LegalFormOption[];
  verein?: boolean;
  /** Der neue Kontakt kommt in den Haushalt des Elternkontakts. */
  imHaushalt?: boolean;
  /** Schon gespeicherter Elternkontakt — fehlt bei einer laufenden Neuanlage. */
  elternId?: number;
  /** FO/BO des Elternkontakts (aus dessen Formular oder Akte). */
  ownership?: Ownership | null;
  /** Wohnanschrift des Elternkontakts — für ein neues Haushaltsmitglied. */
  haushaltsAnschrift?: SharedAddress | null;
};

/**
 * Alles, was die Vollanlage aus der Schnellanlage mitbekommt: Art, Felder und
 * der Bezug für den POST.
 *
 * - FO/BO des Elternkontakts stehen sichtbar in der Maske (sie sendet ihre
 *   FO/BO ohnehin immer mit, eine Server-Vererbung allein griffe deshalb nie).
 * - Ein Haushaltsmitglied bekommt die Rolle „im selben Haushalt" und — ohne
 *   eigene Anschrift — die des Haushalts, wie in der Schnellanlage.
 * - `zusatzPayload` nur mit gespeichertem Elternkontakt; vorher gibt es keine
 *   Id, auf die sich der Server beziehen könnte.
 */
export function vollanlageVorbereiten(e: VollanlageEingabe): Vorbelegung & { zusatzPayload: ZusatzPayload } {
  const basis = refZuVorbelegung(e.ref, { rechtsformOptionen: e.rechtsformOptionen, verein: e.verein });
  const initialValues: Record<string, string> = {
    ...ownershipZuVorbelegung(e.ownership),
    ...basis.initialValues,
  };
  const haushalt = !!e.imHaushalt && basis.kind === "private";
  if (haushalt) {
    initialValues.kontaktStatus = "Sonstige";
    initialValues.kontaktStatusRollen = "Haushaltsmitglied";
    const eigene = ["street", "postalcode", "city"].some((k) => initialValues[k]);
    const a = e.haushaltsAnschrift;
    if (!eigene && a) {
      setze(initialValues, "street", a.street);
      setze(initialValues, "postalcode", a.postalCode);
      setze(initialValues, "city", a.city);
      setze(initialValues, "cityArea", a.ortsteil);
      setze(initialValues, "country", a.country);
    }
  }
  const elternId = gueltigeId(e.elternId);
  const zusatzPayload: ZusatzPayload = elternId
    ? { verantwortungVonKontaktId: elternId, ...(haushalt ? { imHaushaltVon: elternId } : {}) }
    : {};
  return { kind: basis.kind, initialValues, zusatzPayload };
}

/**
 * Nach dem Speichern der Vollanlage: der neue Kontakt als BESTEHENDER Ref
 * (`contactId`, ohne `manual`) — so legt das Ausgangsformular beim Speichern
 * nur noch die Beziehung an, keinen zweiten Kontakt.
 */
export function kontaktRefAusFormular(kind: VollanlageArt, contactId: number, felder: FormState | Record<string, unknown> = {}): ContactRef {
  const f = (k: string) => t((felder as Record<string, unknown>)[k]);
  if (kind === "company") {
    const name = f("name") || f("companyName") || f("lastname");
    const legalFormId = Number(f("legalFormId"));
    return {
      contactId,
      type: "Firma",
      lastName: name,
      displayName: name || "Neue Firma",
      ...(f("gesellschaftsform") ? { legalForm: f("gesellschaftsform") } : {}),
      ...(Number.isInteger(legalFormId) && legalFormId > 0 ? { legalFormId } : {}),
    };
  }
  const nachname = f("lastname");
  const vorname = f("firstname");
  const anrede = f("anrede");
  return {
    contactId,
    type: "Person",
    lastName: nachname,
    ...(vorname ? { firstName: vorname } : {}),
    displayName: [nachname, vorname].filter(Boolean).join(", ") || "Neue Person",
    ...(anrede ? { salutationLabel: anrede } : {}),
    ...(geschlechtAusAnrede(anrede) ? { gender: geschlechtAusAnrede(anrede) } : {}),
    ...(f("dateofbirth") ? { birthDate: f("dateofbirth").slice(0, 10) } : {}),
  };
}
